// Mobile card view for FP Directory

import React from 'react'
import { Box, Card, CardContent, Typography, Stack, Chip } from '@mui/material'

import { poolwerxColors } from '../../../theme/colors'
import type { FranchisePartner, FPStatus } from '../types'
import { getStatusColor } from '../utils'
import QuickLinks from './QuickLinks'
import ContactCell from './ContactCell'

interface FPMobileCardProps {
  fp: FranchisePartner
}

const FPMobileCard: React.FC<FPMobileCardProps> = ({ fp }) => (
  <Card sx={{ mb: 1.5 }}>
    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
      {/* Header row: Code, Country, Status chips */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontWeight: 600,
            color: poolwerxColors.teal
          }}
        >
          {fp.fp_code.toUpperCase()}
        </Typography>
        <Chip
          label={fp.country}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.7rem',
            backgroundColor: fp.country === 'USA' ? '#e3f2fd' : '#e8f5e9'
          }}
        />
        {fp.fp_status && (
          <Chip
            label={fp.fp_status}
            size="small"
            color={getStatusColor(fp.fp_status as FPStatus)}
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        )}
        {fp.is_icu && (
          <Chip
            label="ICU"
            size="small"
            color="warning"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        )}
        {fp.is_selling && (
          <Chip
            label="Selling"
            size="small"
            color="info"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        )}
      </Stack>

      {/* Franchise name */}
      <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }} noWrap>
        {fp.franchise_name}
      </Typography>

      {/* Region/BDM info */}
      {(fp.region_name || fp.bdm_name) && (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
          {[fp.region_name, fp.bdm_name].filter(Boolean).join(' • ')}
        </Typography>
      )}

      {/* Contact and Quick Links row */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ContactCell fp={fp} />
        </Box>
        <Box sx={{ ml: 1 }}>
          <QuickLinks fp={fp} />
        </Box>
      </Stack>
    </CardContent>
  </Card>
)

export default FPMobileCard
